import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, Tag, ShoppingCart, BookOpen, Truck, RotateCcw, DollarSign, Eye,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import { SAUDI_BRAND } from '../../products/saudi/config/saudiBrandConfig';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { getBrandVatRate } from '../../shared/utils/brandCurrency';
import { DEFAULT_VENTES_CONFIG, normalizeVentesConfig } from './ventesConfigDefaults';
import { syncVentesConfigToSettings } from './useVentesConfig';

const NS = 'modules.ventes.pages.ventesConfigPage';

function ConfigSection({ icon: Icon, title, hint, children }) {
  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <Icon className="h-6 w-6 shrink-0" />
        {title}
      </h2>
      {hint ? <p className="text-white/50 text-sm mb-4 text-right">{hint}</p> : <div className="mb-4" />}
      {children}
    </div>
  );
}

function ConfigCheckbox({ checked, onChange, label }) {
  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
      />
      <label className="ml-3 text-white/80">{label}</label>
    </div>
  );
}

function ConfigField({ label, hint, children }) {
  return (
    <div>
      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
      {children}
      {hint ? <p className="text-white/50 text-xs mt-1 text-right">{hint}</p> : null}
    </div>
  );
}

const inputClass = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const VentesConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [ventesConfig, setVentesConfig] = useState(DEFAULT_VENTES_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('pricing');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/ventes/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeVentesConfig(raw || {});
      setVentesConfig(merged);
      syncVentesConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading ventes config:', error);
      const fallback = normalizeVentesConfig({});
      setVentesConfig(fallback);
      syncVentesConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeVentesConfig(ventesConfig);
      await localApiAdapter.put('/ventes/config', payload);
      syncVentesConfigToSettings(payload, patchSettings, settings);
      setVentesConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving ventes config:', error);
      setSaveError(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const resetConfig = normalizeVentesConfig({});
      setVentesConfig(resetConfig);
      await localApiAdapter.put('/ventes/config', resetConfig);
      syncVentesConfigToSettings(resetConfig, patchSettings, settings);
    } catch (error) {
      console.error('Error resetting ventes config:', error);
      alert(p('reset_error'));
    }
  };

  const updateConfig = (key, value) => {
    setVentesConfig((prev) => {
      const next = normalizeVentesConfig({ ...prev, [key]: value });
      syncVentesConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const pricingTab = (
    <div className="space-y-6">
      <ConfigSection icon={Tag} title={p('tab_pricing')} hint={p('section_pricing_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('price_rule_reference_prefix')} hint={p('price_rule_reference_prefix_hint')}>
            <input
              type="text"
              value={ventesConfig.priceRuleReferencePrefix}
              onChange={(e) => updateConfig('priceRuleReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={ventesConfig.enablePriceRules}
              onChange={(v) => updateConfig('enablePriceRules', v)}
              label={p('enable_price_rules')}
            />
            <ConfigCheckbox
              checked={ventesConfig.autoGeneratePriceRuleReference}
              onChange={(v) => updateConfig('autoGeneratePriceRuleReference', v)}
              label={p('auto_generate_price_rule_reference')}
            />
            <ConfigCheckbox
              checked={ventesConfig.allowStackableDiscounts}
              onChange={(v) => updateConfig('allowStackableDiscounts', v)}
              label={p('check_stackable_discounts')}
            />
            <ConfigCheckbox
              checked={ventesConfig.requirePromoCodeApproval}
              onChange={(v) => updateConfig('requirePromoCodeApproval', v)}
              label={p('check_promo_approval')}
            />
            <ConfigCheckbox
              checked={ventesConfig.defaultPriceIncludesVat}
              onChange={(v) => updateConfig('defaultPriceIncludesVat', v)}
              label={p('default_price_includes_vat')}
            />
          </div>
          <ConfigField label={p('default_discount_type')} hint={p('default_discount_type_hint')}>
            <select
              value={ventesConfig.defaultDiscountType}
              onChange={(e) => updateConfig('defaultDiscountType', e.target.value)}
              className={inputClass}
            >
              <option value="percentage" className="text-gray-900">{p('discount_type_percentage')}</option>
              <option value="fixed_amount" className="text-gray-900">{p('discount_type_fixed')}</option>
            </select>
          </ConfigField>
        </div>
      </ConfigSection>

      <ConfigSection icon={Settings} title={p('section_general')} hint={p('section_general_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('label_default_currency')}>
            <BrandCurrencySelect
              value={ventesConfig.defaultCurrency}
              onChange={(e) => updateConfig('defaultCurrency', e.target.value)}
            />
          </ConfigField>
          <ConfigField label={p('label_vat_rate')} hint={p('label_vat_rate_hint', { rate: SAUDI_BRAND.vatRate })}>
            <input
              type="text"
              value={`${ventesConfig.defaultVatRate ?? getBrandVatRate()}%`}
              disabled
              className={`${inputClass} text-white/60 cursor-not-allowed`}
            />
          </ConfigField>
          <ConfigField label={p('label_sales_target')}>
            <input
              type="number"
              min={0}
              value={ventesConfig.defaultSalesTarget ?? 0}
              onChange={(e) => updateConfig('defaultSalesTarget', parseInt(e.target.value, 10) || 0)}
              className={inputClass}
            />
          </ConfigField>
        </div>
      </ConfigSection>
    </div>
  );

  const ordersTab = (
    <ConfigSection icon={ShoppingCart} title={p('tab_orders')} hint={p('section_orders_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('order_reference_prefix')} hint={p('order_reference_prefix_hint')}>
          <input
            type="text"
            value={ventesConfig.orderReferencePrefix}
            onChange={(e) => updateConfig('orderReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('label_payment_terms')}>
          <input
            type="text"
            value={ventesConfig.defaultPaymentTerms || ''}
            onChange={(e) => updateConfig('defaultPaymentTerms', e.target.value)}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('label_lead_time')}>
          <input
            type="number"
            min={0}
            value={ventesConfig.defaultDeliveryLeadTimeDays ?? 5}
            onChange={(e) => updateConfig('defaultDeliveryLeadTimeDays', parseInt(e.target.value, 10) || 0)}
            className={inputClass}
          />
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={ventesConfig.enableOrders}
            onChange={(v) => updateConfig('enableOrders', v)}
            label={p('enable_orders')}
          />
          <ConfigCheckbox
            checked={ventesConfig.autoGenerateOrderReference}
            onChange={(v) => updateConfig('autoGenerateOrderReference', v)}
            label={p('check_auto_order_number')}
          />
          <ConfigCheckbox
            checked={ventesConfig.requireCustomer}
            onChange={(v) => updateConfig('requireCustomer', v)}
            label={p('check_require_customer')}
          />
          <ConfigCheckbox
            checked={ventesConfig.allowPartialDelivery}
            onChange={(v) => updateConfig('allowPartialDelivery', v)}
            label={p('check_partial_delivery')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const ledgerTab = (
    <ConfigSection icon={BookOpen} title={p('tab_ledger')} hint={p('section_ledger_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('sale_reference_prefix')} hint={p('sale_reference_prefix_hint')}>
          <input
            type="text"
            value={ventesConfig.saleReferencePrefix}
            onChange={(e) => updateConfig('saleReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('default_payment_status')}>
          <select
            value={ventesConfig.defaultPaymentStatus}
            onChange={(e) => updateConfig('defaultPaymentStatus', e.target.value)}
            className={inputClass}
          >
            <option value="pending" className="text-gray-900">{p('payment_status_pending')}</option>
            <option value="paid" className="text-gray-900">{p('payment_status_paid')}</option>
            <option value="partial" className="text-gray-900">{p('payment_status_partial')}</option>
          </select>
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={ventesConfig.enableSalesLedger}
            onChange={(v) => updateConfig('enableSalesLedger', v)}
            label={p('enable_sales_ledger')}
          />
          <ConfigCheckbox
            checked={ventesConfig.autoGenerateSaleReference}
            onChange={(v) => updateConfig('autoGenerateSaleReference', v)}
            label={p('auto_generate_sale_reference')}
          />
          <ConfigCheckbox
            checked={ventesConfig.autoConvertDeliveredOrders}
            onChange={(v) => updateConfig('autoConvertDeliveredOrders', v)}
            label={p('auto_convert_delivered_orders')}
          />
          <ConfigCheckbox
            checked={ventesConfig.showTaxBreakdown}
            onChange={(v) => updateConfig('showTaxBreakdown', v)}
            label={p('check_show_tax_breakdown')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const deliveriesTab = (
    <ConfigSection icon={Truck} title={p('tab_deliveries')} hint={p('section_deliveries_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('delivery_reference_prefix')} hint={p('delivery_reference_prefix_hint')}>
          <input
            type="text"
            value={ventesConfig.deliveryReferencePrefix}
            onChange={(e) => updateConfig('deliveryReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={ventesConfig.enableDeliveries}
            onChange={(v) => updateConfig('enableDeliveries', v)}
            label={p('enable_deliveries')}
          />
          <ConfigCheckbox
            checked={ventesConfig.autoGenerateDeliveryReference}
            onChange={(v) => updateConfig('autoGenerateDeliveryReference', v)}
            label={p('auto_generate_delivery_reference')}
          />
          <ConfigCheckbox
            checked={ventesConfig.signatureRequired}
            onChange={(v) => updateConfig('signatureRequired', v)}
            label={p('check_signature_required')}
          />
          <ConfigCheckbox
            checked={ventesConfig.trackDeliveries}
            onChange={(v) => updateConfig('trackDeliveries', v)}
            label={p('check_track_deliveries')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const returnsTab = (
    <ConfigSection icon={RotateCcw} title={p('tab_returns')} hint={p('section_returns_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('return_reference_prefix')} hint={p('return_reference_prefix_hint')}>
          <input
            type="text"
            value={ventesConfig.returnReferencePrefix}
            onChange={(e) => updateConfig('returnReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('label_return_limit')}>
          <input
            type="number"
            min={0}
            value={ventesConfig.returnTimeLimitDays}
            onChange={(e) => updateConfig('returnTimeLimitDays', parseInt(e.target.value, 10) || 0)}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('default_refund_method')}>
          <select
            value={ventesConfig.defaultRefundMethod}
            onChange={(e) => updateConfig('defaultRefundMethod', e.target.value)}
            className={inputClass}
          >
            <option value="bank_transfer" className="text-gray-900">{p('refund_bank_transfer')}</option>
            <option value="cash" className="text-gray-900">{p('refund_cash')}</option>
            <option value="credit_note" className="text-gray-900">{p('refund_credit_note')}</option>
            <option value="mada" className="text-gray-900">{p('refund_mada')}</option>
          </select>
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={ventesConfig.enableReturns}
            onChange={(v) => updateConfig('enableReturns', v)}
            label={p('enable_returns')}
          />
          <ConfigCheckbox
            checked={ventesConfig.autoGenerateReturnReference}
            onChange={(v) => updateConfig('autoGenerateReturnReference', v)}
            label={p('auto_generate_return_reference')}
          />
          <ConfigCheckbox
            checked={ventesConfig.requireReturnApproval}
            onChange={(v) => updateConfig('requireReturnApproval', v)}
            label={p('require_return_approval')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const commissionsTab = (
    <div className="space-y-6">
      <ConfigSection icon={DollarSign} title={p('tab_commissions')} hint={p('section_commissions_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('commission_reference_prefix')} hint={p('commission_reference_prefix_hint')}>
            <input
              type="text"
              value={ventesConfig.commissionReferencePrefix}
              onChange={(e) => updateConfig('commissionReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <ConfigField label={p('label_commission_rate')}>
            <input
              type="number"
              min={0}
              max={100}
              value={ventesConfig.defaultCommissionRate}
              onChange={(e) => updateConfig('defaultCommissionRate', parseInt(e.target.value, 10) || 0)}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={ventesConfig.enableCommissions}
              onChange={(v) => updateConfig('enableCommissions', v)}
              label={p('enable_commissions')}
            />
            <ConfigCheckbox
              checked={ventesConfig.autoGenerateCommissionReference}
              onChange={(v) => updateConfig('autoGenerateCommissionReference', v)}
              label={p('auto_generate_commission_reference')}
            />
            <ConfigCheckbox
              checked={ventesConfig.autoCalculateOnSale}
              onChange={(v) => updateConfig('autoCalculateOnSale', v)}
              label={p('check_auto_commission')}
            />
            <ConfigCheckbox
              checked={ventesConfig.requireApprovalBeforePayment}
              onChange={(v) => updateConfig('requireApprovalBeforePayment', v)}
              label={p('require_commission_approval')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={Eye} title={p('section_display')} hint={p('section_display_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('items_per_page')} hint={p('items_per_page_hint')}>
            <select
              value={ventesConfig.itemsPerPage}
              onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))}
              className={inputClass}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n} className="text-gray-900">{n}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label={p('default_view')} hint={p('default_view_hint')}>
            <select
              value={ventesConfig.defaultView}
              onChange={(e) => updateConfig('defaultView', e.target.value)}
              className={inputClass}
            >
              <option value="table" className="text-gray-900">{p('view_table')}</option>
              <option value="cards" className="text-gray-900">{p('view_cards')}</option>
            </select>
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={ventesConfig.enablePipeline !== false}
              onChange={(v) => updateConfig('enablePipeline', v)}
              label={p('enable_pipeline', { defaultValue: 'تفعيل المسار التجاري' })}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const tabs = [
    { id: 'pricing', label: p('tab_pricing'), icon: Tag },
    { id: 'orders', label: p('tab_orders'), icon: ShoppingCart },
    { id: 'ledger', label: p('tab_ledger'), icon: BookOpen },
    { id: 'deliveries', label: p('tab_deliveries'), icon: Truck },
    { id: 'returns', label: p('tab_returns'), icon: RotateCcw },
    { id: 'commissions', label: p('tab_commissions'), icon: DollarSign },
  ];

  const tabContent = {
    pricing: pricingTab,
    orders: ordersTab,
    ledger: ledgerTab,
    deliveries: deliveriesTab,
    returns: returnsTab,
    commissions: commissionsTab,
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved_success')}
      saveError={saveError}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default VentesConfigPage;
